import {
  EMPTY_NODE,
  Fragment,
  NODE_TYPE_CUSTOM,
  NODE_TYPE_EMPTY,
  NODE_TYPE_FRAGMENT,
  NODE_TYPE_TAG,
  NODE_TYPE_TEXT,
  createElement,
  getBNodeKey,
  getBNodeParent,
  getBNodeSlot,
  getVNodeKey,
  isReservedTagProp,
  normalizeComponentChildren,
  setBNodeParent
} from "./chunk-RI43RBO2.js";
import {
  once,
  tinyassert
} from "./chunk-QB7S5DDF.js";

// src/hooks.ts
function resolveNextState(prev, next) {
  return typeof next === "function" ? next(prev) : next;
}
var HookContext = class _HookContext {
  // reconciler use `notify` for "force update"
  constructor(notify) {
    this.notify = notify;
  }
  // expose global
  // (technically we can keep stack HookContext[] so that HookContext.wrap can be reentrant)
  static current;
  initial = true;
  hookCount = 0;
  hooks = [];
  wrap(f) {
    tinyassert(!_HookContext.current, "hook reentrance?");
    _HookContext.current = this;
    this.hookCount = 0;
    try {
      return f();
    } finally {
      this.initial = false;
      tinyassert(_HookContext.current);
      _HookContext.current = void 0;
    }
  }
  runEffect(type) {
    for (const hook of this.hooks) {
      if (hook.type === type) {
        hook.pendings.forEach((pending, i) => {
          if (pending.effect) {
            tinyassert(!pending.cleanup);
            pending.cleanup = pending.effect();
            pending.effect = void 0;
          }
          if (i < hook.pendings.length - 1) {
            if (pending.cleanup) {
              pending.cleanup();
            }
          }
        });
        hook.pendings = hook.pendings.slice(-1);
      }
    }
  }
  cleanupEffect(type) {
    for (const hook of this.hooks) {
      if (hook.type === type) {
        for (const pending of hook.pendings) {
          if (pending.cleanup) {
            pending.cleanup();
          }
        }
        hook.pendings = [];
      }
    }
  }
  //
  // hook api
  //
  useReducer = (reducer, initialState) => {
    if (this.initial) {
      this.hooks.push({
        type: "reducer",
        state: resolveNextState(void 0, initialState)
      });
    }
    const hook = this.hooks[this.hookCount++];
    tinyassert(hook.type === "reducer");
    const state = hook.state;
    const dispatch = (action) => {
      const nextState = reducer(hook.state, action);
      if (hook.state !== nextState) {
        hook.state = nextState;
        this.notify();
      }
    };
    return [state, dispatch];
  };
  useEffect = (type, effect, deps) => {
    if (this.initial) {
      this.hooks.push({
        type,
        deps,
        pendings: [{ effect }]
      });
    }
    const hook = this.hooks[this.hookCount++];
    tinyassert(hook.type === type);
    tinyassert(hook.deps?.length === deps?.length);
    if (!this.initial && !(hook.deps && deps && isEqualShallow(hook.deps, deps))) {
      hook.deps = deps;
      hook.pendings.push({ effect });
    }
  };
};
var useReducer = /* @__PURE__ */ defineHook((ctx) => ctx.useReducer);
var useEffectInner = /* @__PURE__ */ defineHook((ctx) => ctx.useEffect);
function useLayoutEffect(effect, deps) {
  return useEffectInner("layout-effect", effect, deps);
}
function useEffect(effect, deps) {
  return useEffectInner("effect", effect, deps);
}
function useState(initialState) {
  return useReducer(
    (prev, next) => resolveNextState(prev, next),
    initialState
  );
}
function useRef(initialState) {
  return useState(() => ({ current: initialState }))[0];
}
function useMemo(callback, deps) {
  const ref = useRef({
    initial: true,
    value: void 0,
    deps
  });
  if (ref.current.initial || !isEqualShallow(ref.current.deps, deps)) {
    ref.current.initial = false;
    ref.current.value = callback();
    ref.current.deps = deps;
  }
  return ref.current.value;
}
function useCallback(callback, deps) {
  return useMemo(() => callback, deps);
}
function defineHook(implement) {
  return new Proxy(() => {
  }, {
    apply(_target, ...args) {
      tinyassert(HookContext.current);
      return Reflect.apply(implement(HookContext.current), ...args);
    }
  });
}
function isEqualShallow(xs, ys) {
  return xs.length === ys.length && xs.every((x, i) => x === ys[i]);
}

// src/reconciler.ts
function render(vnode, parent, bnode = EMPTY_NODE, hydration = false) {
  const effectManager = new EffectManager();
  const newBnode = reconcileNode(
    vnode,
    bnode,
    parent,
    void 0,
    effectManager,
    hydration
  );
  effectManager.run();
  return newBnode;
}
function hydrate(vnode, parent) {
  return render(vnode, parent, void 0, true);
}
function reconcileNode(vnode, bnode, hparent, preSlot, effectManager, isHydrate) {
  if (isHydrate) {
    tinyassert(bnode.type === NODE_TYPE_EMPTY);
    bnode = hydrateNode(vnode, hparent, preSlot);
    preSlot = getBNodeSlot(bnode) ?? preSlot;
  }
  if (vnode.type === NODE_TYPE_EMPTY) {
    if (bnode.type === NODE_TYPE_EMPTY) {
    } else {
      unmount(bnode);
      bnode = EMPTY_NODE;
    }
  } else if (vnode.type === NODE_TYPE_TAG) {
    let queueRef = isHydrate;
    if (bnode.type === NODE_TYPE_TAG && bnode.vnode.key === vnode.key && bnode.vnode.name === vnode.name && bnode.vnode.props.ref === vnode.props.ref) {
      if (isHydrate) {
        hydrateTagProps(bnode, vnode.props);
      } else {
        reconcileTagProps(bnode, vnode.props, bnode.vnode.props);
      }
      bnode.vnode = vnode;
      bnode.child = reconcileNode(
        normalizeComponentChildren(vnode.props.children),
        bnode.child,
        bnode.hnode,
        void 0,
        effectManager,
        isHydrate
      );
      placeChild(bnode.hnode, hparent, preSlot, false);
    } else {
      queueRef = true;
      unmount(bnode);
      const hnode = document.createElement(vnode.name);
      const child = reconcileNode(
        normalizeComponentChildren(vnode.props.children),
        EMPTY_NODE,
        hnode,
        void 0,
        effectManager,
        isHydrate
      );
      bnode = {
        type: vnode.type,
        vnode,
        child,
        hnode,
        listeners: /* @__PURE__ */ new Map()
      };
      reconcileTagProps(bnode, vnode.props, {});
      placeChild(bnode.hnode, hparent, preSlot, true);
    }
    if (queueRef) {
      effectManager.refNodes.push(bnode);
    }
    setBNodeParent(bnode.child, bnode);
  } else if (vnode.type === NODE_TYPE_TEXT) {
    if (bnode.type === NODE_TYPE_TEXT) {
      if (bnode.vnode.data !== vnode.data) {
        bnode.hnode.data = vnode.data;
      }
      bnode.vnode = vnode;
      placeChild(bnode.hnode, hparent, preSlot, false);
    } else {
      unmount(bnode);
      const hnode = document.createTextNode(vnode.data);
      bnode = {
        type: vnode.type,
        vnode,
        hnode
      };
      placeChild(bnode.hnode, hparent, preSlot, true);
    }
  } else if (vnode.type === NODE_TYPE_FRAGMENT) {
    if (bnode.type === NODE_TYPE_FRAGMENT && bnode.vnode.key === vnode.key) {
      const [newChildren, oldChildren] = alignChildrenByKey(
        vnode.children,
        bnode.children
      );
      bnode.children = newChildren;
      for (const bnode2 of oldChildren) {
        unmount(bnode2);
      }
      bnode.vnode = vnode;
    } else {
      unmount(bnode);
      bnode = {
        type: vnode.type,
        vnode,
        children: [],
        parent: void 0,
        slot: void 0
      };
    }
    const bchildren = bnode.children;
    for (const bchild of bchildren.slice(vnode.children.length)) {
      unmount(bchild);
    }
    bnode.slot = void 0;
    for (let i = 0; i < vnode.children.length; i++) {
      const bchild = reconcileNode(
        vnode.children[i],
        bchildren[i] ?? EMPTY_NODE,
        hparent,
        preSlot,
        effectManager,
        isHydrate
      );
      preSlot = getBNodeSlot(bchild) ?? preSlot;
      bnode.slot = getBNodeSlot(bchild) ?? bnode.slot;
      bnode.children[i] = bchild;
      setBNodeParent(bchild, bnode);
    }
  } else if (vnode.type === NODE_TYPE_CUSTOM) {
    if (bnode.type === NODE_TYPE_CUSTOM && bnode.vnode.key === vnode.key && bnode.vnode.render === vnode.render) {
      bnode.hookContext.notify = updateCustomNodeUnsupported;
      const vchild = bnode.hookContext.wrap(() => vnode.render(vnode.props));
      bnode.child = reconcileNode(
        vchild,
        bnode.child,
        hparent,
        preSlot,
        effectManager,
        isHydrate
      );
      bnode.vnode = vnode;
    } else {
      unmount(bnode);
      const hookContext = new HookContext(updateCustomNodeUnsupported);
      const vchild = hookContext.wrap(() => vnode.render(vnode.props));
      const child = reconcileNode(
        vchild,
        EMPTY_NODE,
        hparent,
        preSlot,
        effectManager,
        isHydrate
      );
      bnode = {
        type: vnode.type,
        vnode,
        child,
        hookContext,
        hparent: void 0,
        parent: void 0,
        slot: void 0
      };
    }
    bnode.hparent = hparent;
    setBNodeParent(bnode.child, bnode);
    bnode.slot = getBNodeSlot(bnode.child);
    effectManager.effectNodes.push(bnode);
    const bcustom = bnode;
    bnode.hookContext.notify = () => {
      updateCustomNode(vnode, bcustom);
    };
  } else {
    vnode;
  }
  return bnode;
}
function hydrateNode(vnode, hparent, preSlot) {
  if (vnode.type === NODE_TYPE_EMPTY) {
    return EMPTY_NODE;
  } else if (vnode.type === NODE_TYPE_TAG) {
    const hnode = getSlotTargetNode(hparent, preSlot);
    tinyassert(
      hnode instanceof Element && hnode.tagName.toLowerCase() === vnode.name,
      `tag hydration mismatch (actual: '${hnode?.nodeName}', expected: '${vnode.name}')`
    );
    return {
      type: vnode.type,
      vnode,
      child: EMPTY_NODE,
      hnode,
      listeners: /* @__PURE__ */ new Map()
    };
  } else if (vnode.type === NODE_TYPE_TEXT) {
    const hnode = getSlotTargetNode(hparent, preSlot);
    tinyassert(
      hnode instanceof Text,
      `text hydration mismatch (actual: '${hnode?.nodeName}', expected: '#text')`
    );
    tinyassert(
      hnode.data === vnode.data,
      `text hydration mismatch (actual: '${hnode.data}', expected: '${vnode.data}')`
    );
    return { type: vnode.type, vnode, hnode };
  } else if (vnode.type === NODE_TYPE_FRAGMENT) {
    return { type: vnode.type, vnode, children: [] };
  } else if (vnode.type === NODE_TYPE_CUSTOM) {
    return {
      type: vnode.type,
      vnode,
      child: EMPTY_NODE,
      hookContext: new HookContext(updateCustomNodeUnsupported),
      hparent: void 0,
      parent: void 0,
      slot: void 0
    };
  }
  return vnode;
}
function placeChild(hnode, hparent, preSlot, init) {
  const slotNext = getSlotTargetNode(hparent, preSlot);
  if (init || !(hnode === slotNext || hnode.nextSibling === slotNext)) {
    hparent.insertBefore(hnode, slotNext);
  }
}
function getSlotTargetNode(hparent, preSlot) {
  return preSlot ? preSlot.nextSibling : hparent.firstChild;
}
function updateCustomNodeUnsupported() {
  throw new Error("Unsupported force-update during render");
}
function updateCustomNode(vnode, bnode) {
  if (!bnode.hparent) {
    return;
  }
  const oldSlot = getBNodeSlot(bnode);
  const preSlot = findPreviousSlot(bnode);
  const effectManager = new EffectManager();
  const newBnode = reconcileNode(
    vnode,
    bnode,
    bnode.hparent,
    preSlot,
    effectManager,
    false
  );
  tinyassert(newBnode === bnode);
  const newSlot = getBNodeSlot(bnode);
  if (oldSlot !== newSlot) {
    updateParentSlot(bnode);
  }
  effectManager.run();
}
function findPreviousSlot(child) {
  let parent = getBNodeParent(child);
  while (parent) {
    if (parent.type === NODE_TYPE_TAG) {
      return;
    }
    if (parent.type === NODE_TYPE_FRAGMENT) {
      let slot;
      for (const c of parent.children) {
        if (c === child) {
          if (slot) {
            return slot;
          }
          break;
        }
        slot = getBNodeSlot(c) ?? slot;
      }
    }
    child = parent;
    parent = child.parent;
  }
  return;
}
function updateParentSlot(child) {
  let parent = getBNodeParent(child);
  while (parent) {
    if (parent.type === NODE_TYPE_TAG) {
      return;
    }
    if (parent.type === NODE_TYPE_CUSTOM) {
      parent.slot = getBNodeSlot(child);
    }
    if (parent.type === NODE_TYPE_FRAGMENT) {
      let slot;
      for (const c of parent.children) {
        slot = getBNodeSlot(c) ?? slot;
      }
      parent.slot = slot;
    }
    child = parent;
    parent = child.parent;
  }
}
function alignChildrenByKey(vnodes, bnodes) {
  const keyMap = /* @__PURE__ */ new Map();
  vnodes.forEach((vnode, i) => {
    const key = getVNodeKey(vnode);
    if (typeof key !== "undefined") {
      keyMap.set(key, i);
    }
  });
  if (keyMap.size !== vnodes.length) {
    return [bnodes, []];
  }
  const newBnodes = vnodes.map(() => EMPTY_NODE);
  const oldBnodes = [];
  for (const bnode of bnodes) {
    const key = getBNodeKey(bnode);
    if (typeof key !== "undefined") {
      const i = keyMap.get(key);
      if (typeof i !== "undefined") {
        newBnodes[i] = bnode;
        continue;
      }
    }
    oldBnodes.push(bnode);
  }
  return [newBnodes, oldBnodes];
}
function reconcileTagProps(bnode, props, oldProps) {
  for (const k in oldProps) {
    if (!(k in props)) {
      setTagProp(bnode, k, null);
    }
  }
  for (const k in props) {
    if (props[k] !== oldProps[k]) {
      setTagProp(bnode, k, props[k]);
    }
  }
}
function hydrateTagProps(bnode, props) {
  for (const key in props) {
    if (key.startsWith("on")) {
      setTagProp(bnode, key, props[key]);
    }
  }
}
function setTagProp(bnode, key, value) {
  if (isReservedTagProp(key)) {
    return;
  }
  const { listeners, hnode } = bnode;
  if (key.startsWith("on")) {
    const eventType = key.slice(2).toLowerCase();
    const listener = listeners.get(eventType);
    if (listener) {
      hnode.removeEventListener(eventType, listener);
    }
    if (value != null) {
      tinyassert(
        typeof value === "function",
        `Invalid event listener prop for '${eventType}'`
      );
      listeners.set(eventType, value);
      hnode.addEventListener(eventType, value);
    }
    return;
  }
  if (key in hnode) {
    try {
      hnode[key] = value != null ? value : "";
      return;
    } catch {
    }
  }
  if (value != null) {
    hnode.setAttribute(key, value);
  } else {
    hnode.removeAttribute(key);
  }
}
function unmount(bnode) {
  return unmountNode(bnode, false);
}
function unmountNode(bnode, skipRemove) {
  if (bnode.type === NODE_TYPE_EMPTY) {
    return;
  } else if (bnode.type === NODE_TYPE_TAG) {
    unmountNode(
      bnode.child,
      /* skipRemove */
      true
    );
    if (!skipRemove) {
      bnode.hnode.remove();
      if (bnode.vnode.props.ref) {
        bnode.vnode.props.ref(null);
      }
    }
  } else if (bnode.type === NODE_TYPE_TEXT) {
    bnode.hnode.remove();
  } else if (bnode.type === NODE_TYPE_FRAGMENT) {
    for (const child of bnode.children) {
      unmountNode(child, skipRemove);
    }
  } else if (bnode.type === NODE_TYPE_CUSTOM) {
    bnode.hookContext.cleanupEffect("layout-effect");
    bnode.hookContext.cleanupEffect("effect");
    bnode.hparent = void 0;
    unmountNode(bnode.child, skipRemove);
  } else {
    bnode;
  }
}
var EffectManager = class {
  // TODO: effect ordering? (currently DFS exit time ordering)
  refNodes = [];
  effectNodes = [];
  run() {
    for (const tagNode of this.refNodes) {
      if (tagNode.vnode.props.ref) {
        tagNode.vnode.props.ref(tagNode.hnode);
      }
    }
    for (const customNode of this.effectNodes) {
      customNode.hookContext.runEffect("layout-effect");
    }
    requestAnimationFrame(() => {
      for (const customNode of this.effectNodes) {
        if (customNode.hparent) {
          customNode.hookContext.runEffect("effect");
        }
      }
    });
  }
};

// src/helper/hyperscript.ts
var h = /* @__PURE__ */ new Proxy(() => {
}, {
  get(_target, tag, _receiver) {
    return (...args) => createElement(tag, ...args);
  },
  apply(_target, _thisArg, args) {
    return createElement(...args);
  }
});

// src/compat/index.ts
function useSyncExternalStore(subscribe, getSnapshot, _getServerSnapshot) {
  const [value, setValue] = useState(() => getSnapshot());
  const latestRef = useRef({ getSnapshot });
  latestRef.current.getSnapshot = getSnapshot;
  useEffect(() => {
    return subscribe(() => {
      setValue(latestRef.current.getSnapshot());
    });
  }, [subscribe]);
  return value;
}
function createRoot(container) {
  let bnode;
  return {
    render: (vnode) => {
      bnode = render(vnode, container, bnode);
    },
    unmount() {
      render(EMPTY_NODE, container, bnode);
    }
  };
}
function memo(Fc, isEqualProps = objectShallowEqual) {
  function Memo(props) {
    const [state] = useState(() => {
      const state2 = {
        render: (props2) => state2.current.onceFc(props2)
      };
      return state2;
    });
    if (!state.current || !isEqualProps(state.current.vnode.props, props)) {
      state.current = {
        vnode: {
          type: NODE_TYPE_CUSTOM,
          render: state.render,
          props
        },
        onceFc: once(Fc)
      };
    }
    return state.current.vnode;
  }
  Object.defineProperty(Memo, "name", { value: `Memo(${Fc.name})` });
  return Memo;
}
function objectShallowEqual(x, y) {
  for (const k in x) {
    if (!(k in y)) {
      return false;
    }
  }
  for (const k in y) {
    if (x[k] !== y[k]) {
      return false;
    }
  }
  return true;
}

// src/ssr/render.ts
function renderToString(vnode) {
  return new SsrManager().render(vnode);
}
var SsrManager = class {
  selectTagStack = [];
  render(vnode) {
    if (vnode.type === NODE_TYPE_EMPTY) {
      return "";
    } else if (vnode.type === NODE_TYPE_TAG) {
      return this.renderTag(vnode);
    } else if (vnode.type === NODE_TYPE_TEXT) {
      return escapeHtml(vnode.data);
    } else if (vnode.type === NODE_TYPE_CUSTOM) {
      const { render: render2, props } = vnode;
      const hookContext = new HookContext(updateCustomNodeUnsupported);
      const vchild = hookContext.wrap(() => render2(props));
      return this.render(vchild);
    } else if (vnode.type === NODE_TYPE_FRAGMENT) {
      let result = "";
      for (const vchild of vnode.children) {
        result += this.render(vchild);
      }
      return result;
    }
    return vnode;
  }
  // cf. https://github.com/preactjs/preact-render-to-string/blob/ba4f4eb1f81e01ac15aef377ae609059e9b2ffce/src/index.js#L322
  renderTag(vnode) {
    const { name, props } = vnode;
    let openTag = `<${name}`;
    let innerOverride;
    for (let k in props) {
      if (isReservedTagProp(k)) {
        continue;
      }
      let v = props[k];
      if (v == null || k.startsWith("on")) {
        continue;
      }
      if (k === "className") {
        k = "class";
      }
      if (k === "value") {
        if (name === "textarea") {
          innerOverride = escapeHtml(String(v));
          continue;
        }
        if (name === "select") {
          continue;
        }
        if (name === "option") {
          const select = this.selectTagStack.at(-1);
          if (select && select.props["value"] === v) {
            openTag += ` selected="true"`;
          }
        }
      }
      if (k.startsWith("aria")) {
        k = camelToKebab(k);
      } else {
        k = k.toLowerCase();
      }
      v = escapeHtml(String(v));
      openTag += ` ${k}="${v}"`;
    }
    if (VOID_ELEMENTS.has(name)) {
      return openTag + "/>";
    }
    if (typeof innerOverride === "string") {
      return `${openTag}>${innerOverride}</${name}>`;
    }
    if (name === "select") {
      this.selectTagStack.push(vnode);
    }
    const inner = this.render(normalizeComponentChildren(props.children));
    if (name === "select") {
      this.selectTagStack.pop();
    }
    return `${openTag}>${inner}</${name}>`;
  }
};
var VOID_ELEMENTS = /* @__PURE__ */ new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
]);
function camelToKebab(s) {
  return s.replace(/[A-Z]/g, "-$&").toLowerCase();
}
function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#x27;");
}
export {
  Fragment,
  HookContext,
  createElement,
  createRoot,
  escapeHtml,
  h,
  hydrate,
  memo,
  render,
  renderToString,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore
};
